import React from 'react';

import colorsVars from 'styles/colors.module.scss';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { ActivityCell } from 'components/common/Tables/Cells/ActivityCell/ActivityCell';
import { ActivityIdCell } from 'components/common/Tables/Cells/ActivityIdCell/ActivityIdCell';
import { AssigneeCell } from 'components/common/Tables/Cells/AssigneeCell/AssigneeCell';
import { BasicDateCell } from 'components/common/Tables/Cells/BasicDateCell/BasicDateCell';
import { CriticalPathCell } from 'components/common/Tables/Cells/CriticalPathCell/CriticalPathCell';
import { CriticalityScoreCell } from 'components/common/Tables/Cells/CriticalityScoreCell/CriticalityScoreCell';
import { DateCell } from 'components/common/Tables/Cells/DateCell/DateCell';
import { DetailsCell } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';
import { DurationCell } from 'components/common/Tables/Cells/DurationCell/DurationCell';
import { FloatCell } from 'components/common/Tables/Cells/FloatCell/FloatCell';
import { IndicatorsCell } from 'components/common/Tables/Cells/IndicatorsCell/IndicatorsCell';
import { PredecessorsCell } from 'components/common/Tables/Cells/PredecessorsCell/PredecessorsCell';
import { StatusCell } from 'components/common/Tables/Cells/StatusCell/StatusCell';
import { SuccessorsCell } from 'components/common/Tables/Cells/SuccessorsCell/SuccessorsCell';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { VarianceCell } from 'components/common/Tables/Cells/VarianceCell/VarianceCell';
import { SubmittedDurationCompleteCell } from 'components/common/Tables/Cells/submittedDurationCompleteCell/SubmittedDurationCompleteCell';
import { customSortWithNullV8 } from 'components/common/Tables/Tables.utils';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { TrackedUntrackedActionCellWrapper } from 'components/common/TrackedUntrackedAction/trackedUntrackedActionCellWrapper/trackedUntrackedActionCellWrapper';

import { VersionCompareQueryKeys } from 'components/Dashboards/Project/Components/VersionCompare/VersionCompare.consts';
import CircularProgressWithLabel from 'components/DesignSystem/CircularProgressWithLabel';
import { CustomizedTooltip } from 'components/common';

const { color6: orange, textColor } = colorsVars;

const invalidateKeys = VersionCompareQueryKeys;

export const initialState: ITableColumnState = {
    hiddenColumns: [
        'details',
        'predecessors',
        'successors',
        'activityType',
        'lateFinish',
        'lateStart',
        'plannedStartDate',
        'plannedFinishDate',
        'actualStartDate',
        'actualFinishDate',
        'plannedDuration',
        'actualDuration',
        'remainingDuration',
        'completedDuration',
        'durationOverrun',
        'projectWeekStart',
        'projectWeekFinish',
        'projectMonthStart',
        'projectMonthFinish',
        'parentName',
        'grandParentName',
    ],
    sortBy: [{ id: 'float' }],
};

export const columnsV8 = [
    {
        header: 'Activity',
        accessorKey: 'latestVersion.activityName',
        ...columnsCommonConfig.activity,
        cell: ({ row, toggleAllRowsSelected }) => {
            const activityName = row.original.latestVersion?.activityName;
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const isTracked = row.original.latestVersion?.cardBoardViewResponse?.isTracked;
            const taskVersionHashCode = row.original.latestVersion?.cardBoardViewResponse?.taskVersionHashCode;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;
            const { toggleRowSelected } = row;

            return (
                <ActivityCell
                    activityName={activityName}
                    taskId={taskId}
                    isVirtual={isVirtual}
                    toggleRowSelected={toggleRowSelected}
                    toggleAllRowsSelected={toggleAllRowsSelected}
                    trackedUntrackedActionCell={
                        <TrackedUntrackedActionCellWrapper
                            isTracked={isTracked}
                            taskVersionHashCode={taskVersionHashCode}
                            invalidateQueriesKey={invalidateKeys}
                        />
                    }
                />
            );
        },
    },
    {
        header: 'Activity id',
        accessorKey: 'latestVersion.activityName',
        ...columnsCommonConfig.activityId,
        cell: ({ row }) => {
            const activityId = row.original.latestVersion?.activityId;

            return <ActivityIdCell activityId={activityId} />;
        },
    },
    {
        header: 'Risk indicators',
        accessorKey: 'latestVersion.activityName',
        ...columnsCommonConfig.riskIndicators,
        cell: ({ row }) => {
            const cardIndicators = row.original.latestVersion?.cardBoardViewResponse?.cardIndicators;

            return <IndicatorsCell cardIndicators={cardIndicators} />;
        },
    },
    {
        header: 'Status',
        accessorKey: 'latestVersion.cardBoardViewResponse.status.label',
        ...columnsCommonConfig.status,
        cell: ({ row }) => {
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;
            const cardType = row.original.latestVersion?.cardBoardViewResponse?.type?.name;
            const category = row.original.latestVersion?.cardBoardViewResponse?.category;
            const isInterimChanged = row.original.latestVersion?.cardBoardViewResponse?.isInterimChanged;
            const status = row.original.latestVersion?.cardBoardViewResponse?.status;
            const originalStatus = row.original.latestVersion?.cardBoardViewResponse?.originalStatus;

            return (
                <StatusCell
                    status={status}
                    isVirtual={isVirtual}
                    taskId={taskId}
                    originalStatus={originalStatus}
                    invalidateQueriesKey={invalidateKeys}
                    cardType={cardType}
                    category={category}
                    isInterimChanged={isInterimChanged}
                />
            );
        },
    },
    {
        header: 'Assignee',
        accessorKey: 'latestVersion.cardBoardViewResponse.status.label',
        ...columnsCommonConfig.assignee,
        cell: ({ row }) => {
            const assignee = row.original.latestVersion?.cardBoardViewResponse?.assignee;
            const isVirtual = row.original.latestVersion?.cardBoardViewResponse?.isVirtual;
            const taskId = row.original.latestVersion?.cardBoardViewResponse?.id;

            return (
                <AssigneeCell
                    assignee={assignee}
                    taskId={taskId}
                    invalidateQueriesKey={invalidateKeys}
                    isVirtual={isVirtual}
                />
            );
        },
    },
    {
        header: '% Complete',
        ...columnsCommonConfig.submittedDurationComplete,
        accessorKey: 'latestVersion.cardBoardViewResponse.submittedDurationComplete',
        cell: ({ getValue }) => {
            const value = getValue();
            return <SubmittedDurationCompleteCell value={value} />;
        },
    },
    {
        header: 'Activity Type',
        ...columnsCommonConfig.activityType,
        accessorKey: 'latestVersion.cardBoardViewResponse.taskActivityType',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
    {
        header: 'Details/Last Comment',
        accessorKey: 'latestVersion.cardBoardViewResponse.details',
        ...columnsCommonConfig.details,
        cell: ({ row }) => {
            const { details, cardLastComment, id, isVirtual, isGenAiDetails } =
                row.original.latestVersion?.cardBoardViewResponse;
            return (
                <DetailsCell
                    details={details}
                    cardLastComment={cardLastComment}
                    id={id}
                    isVirtual={isVirtual}
                    invalidateQueriesKey={invalidateKeys}
                    isGenAiDetails={isGenAiDetails}
                />
            );
        },
    },
    {
        header: 'Start Date',
        accessorKey: 'changeMap.startDate',
        ...columnsCommonConfig.startDate,
        cell: ({ row }) => {
            return (
                <DateCell
                    latestVersionDate={row.original.latestVersion?.startDate}
                    changeMapDate={row.original.changeMap?.startDate}
                    comparedVersion={row.original.comparedVersion?.startDate}
                    isChangeMap={row.original.changeMap}
                />
            );
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Finish Date',
        accessorKey: 'changeMap.finishDate',
        ...columnsCommonConfig.finishDate,
        cell: ({ row }) => {
            return (
                <DateCell
                    latestVersionDate={row.original.latestVersion?.finishDate}
                    changeMapDate={row.original.changeMap?.finishDate}
                    comparedVersion={row.original.comparedVersion?.finishDate}
                    isChangeMap={row.original.changeMap}
                />
            );
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Duration',
        accessorKey: 'changeMap.duration',
        ...columnsCommonConfig.duration,
        cell: ({ row }) => {
            return (
                <DurationCell
                    changeMap={row.original.changeMap}
                    changeMapDuration={row.original.changeMap?.duration}
                    latestVersionDuration={row.original.latestVersion?.duration}
                    comparedVersionDuration={row.original.comparedVersion?.duration}
                />
            );
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Criticality Score',
        accessorKey: 'changeMap.criticalityScore',
        ...columnsCommonConfig.criticalityScore,
        cell: ({ row }) => {
            return <CriticalityScoreCell row={row} />;
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Float',
        accessorKey: 'changeMap.float',
        ...columnsCommonConfig.float,
        cell: ({ row }) => {
            return <FloatCell row={row} />;
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Critical Path',
        accessorFn: (row) =>
            row.latestVersion?.cardBoardViewResponse?.cardIndicators.isCriticalPath.isIndicatorOn ? 'Yes' : 'No',
        ...columnsCommonConfig.criticalPath,
        cell: ({ row, value }) => {
            return <CriticalPathCell row={row} value={value} />;
        },
    },
    {
        header: 'Pred.',
        accessorKey: 'changeMap.predecessors',
        ...columnsCommonConfig.predecessors,
        cell: ({ row }) => {
            return <PredecessorsCell row={row} />;
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Succ.',
        accessorKey: 'changeMap.successors',
        ...columnsCommonConfig.successors,
        cell: ({ row }) => {
            return <SuccessorsCell row={row} />;
        },
        sortFn: customSortWithNullV8,
    },
    {
        header: 'Finish Variance',
        ...columnsCommonConfig.lateFinish,
        accessorKey: 'latestVersion.cardBoardViewResponse.finishVariance',
        cell: ({ getValue }) => {
            return <VarianceCell value={getValue()} />;
        },
    },
    {
        header: 'Start Variance',
        ...columnsCommonConfig.lateStart,
        accessorKey: 'latestVersion.cardBoardViewResponse.startVariance',
        cell: ({ getValue }) => {
            return <VarianceCell value={getValue()} />;
        },
    },
    {
        header: 'Planned Start Date ',
        ...columnsCommonConfig.plannedStartDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.plannedStartDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Planned Finish Date',
        ...columnsCommonConfig.plannedFinishDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.plannedFinishDate',
        cell: ({ value }) => {
            return <BasicDateCell value={value} />;
        },
    },
    {
        header: 'Actual Start Date',
        ...columnsCommonConfig.actualStartDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.actualStartDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Actual Finish Date',
        ...columnsCommonConfig.actualFinishDate,
        accessorKey: 'latestVersion.cardBoardViewResponse.actualFinishDate',
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue()} />;
        },
    },
    {
        header: 'Planned Duration',
        ...columnsCommonConfig.plannedDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.plannedDurationInDays',
    },
    {
        header: 'Actual Duration',
        ...columnsCommonConfig.actualDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.actualDurationInDays',
    },
    {
        header: 'Remaining Duration',
        ...columnsCommonConfig.remainingDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.remainingDurationInDays',
    },
    {
        header: 'Current Duration',
        ...columnsCommonConfig.completedDuration,
        accessorKey: 'latestVersion.cardBoardViewResponse.completedDurationInDays',
    },
    {
        header: 'Current Duration Overrun',
        ...columnsCommonConfig.durationOverrun,
        accessorKey: 'latestVersion.cardBoardViewResponse.completedDurationOverrun',
        cell: ({ getValue }) => {
            const color = getValue() > 0 ? orange : textColor;
            return <span style={{ color }}>{getValue()}</span>;
        },
    },
    {
        header: 'Project Week Start ',
        ...columnsCommonConfig.projectWeekStart,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectWeekStart',
    },
    {
        header: 'Project Week Finish',
        ...columnsCommonConfig.projectWeekFinish,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectWeekFinish',
    },
    {
        header: 'Project Month Start',
        ...columnsCommonConfig.projectMonthStart,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectMonthStart',
    },
    {
        header: 'Project Month Finish',
        ...columnsCommonConfig.projectMonthFinish,
        accessorKey: 'latestVersion.cardBoardViewResponse.projectMonthFinish',
    },
    {
        header: 'Immediate Parent Name ',
        ...columnsCommonConfig.parentName,
        accessorKey: 'latestVersion.cardBoardViewResponse.parentActivity',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
    {
        header: 'Grand Parent Name',
        ...columnsCommonConfig.grandParentName,
        accessorKey: 'latestVersion.cardBoardViewResponse.grandParentActivity',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue()} />,
    },
];
